import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fullAccess } from '../../data/teams';
import { addBudgetToFirestore, addCollectionToFirestore, addCollectionToStudioProject, addCrewToFirestore, addEventToFirestore, addExpenseToFirestore, addPaymentToFirestore, addProjectToStudio, deleteCollectionFromFirestore, deleteFileFromFirestoreAndStorage, deleteProjectFromFirestore, fetchInvitationFromFirebase, fetchProjectsFromFirestore, updateCollectionNameInFirestore, updateCollectionSelectionStatusByCollectionIdInFirestore, updateSelectionGalleryStatusByCollectionIdInFirestore, updateCollectionStatusByCollectionIdInFirestore, updateInvitationInFirebase, updateProjectNameInFirestore, updateProjectStatusInFirestore, updateProjectStorageToArchive, restoreProjectFromArchive, toggleFileFavoriteInFirestore, addProjectActivityLogInFirestore } from '../../firebase/functions/firestore';
import { approveExtensionRequest } from '../../firebase/functions/studios';
import { showAlert } from './alertSlice';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/app';
import { convertTimestamps } from '../../utils/firestoreUtils';


const initialState = {
  data: [],
  status: 'login',
  loading : false,
  updatingCollections: {},
  error: null,
};
// Projects
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async ({ currentDomain }) => {
    const projects = await fetchProjectsFromFirestore(currentDomain);

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const processedProjects = await Promise.all(projects.map(async (project) => {
      // Ensure createdAt exists and is a valid timestamp
      if (project.createdAt && typeof project.createdAt === 'number') {
        const now = Date.now();
        const lastRestoredAt = project.storage?.lastRestoredAt ? new Date(project.storage.lastRestoredAt) : null;
        
        // 1. Calculate ARCHIVE threshold (Gallery Validity months)
        // Past this -> Archive status (moves to cold storage)
        const validityMonths = parseInt(project.projectValidityMonths || '6');
        const archiveThresholdDate = new Date(project.createdAt);
        archiveThresholdDate.setMonth(archiveThresholdDate.getMonth() + validityMonths);
        
        // 2. Calculate FINAL EXPIRY threshold (File Retention years + 30-day grace)
        // Past this -> Expired status (Public access ends)
        const retentionYears = parseInt(project.fileRetentionYears || '1');
        const finalExpiryThresholdDate = new Date(project.createdAt);
        finalExpiryThresholdDate.setMonth(finalExpiryThresholdDate.getMonth() + (retentionYears * 12));
        finalExpiryThresholdDate.setDate(finalExpiryThresholdDate.getDate() + 30);

        const isArchivePassed = now > archiveThresholdDate.getTime();
        const isFinalExpiryPassed = now > finalExpiryThresholdDate.getTime();

        let updatedProject = { ...project };

        // Process Final Expiry (Stage 2)
        if (isFinalExpiryPassed && project.status !== 'expired') {
          await updateProjectStatusInFirestore(currentDomain, project.id, 'expired');
          updatedProject.status = 'expired';
        } 
        // Process Archiving (Stage 1) - only if not already archived/expired
        else if (isArchivePassed && project.storage?.status !== 'archive' && project.status !== 'expired') {
          // Check if it was restored recently (30-day lock)
          if (!lastRestoredAt || lastRestoredAt < thirtyDaysAgo) {
            await updateProjectStorageToArchive(currentDomain, project.id);
            
            const newStorageHistoryEntry = {
              status: 'archive',
              dateMoved: now,
            };

            updatedProject.status = 'archive';
            updatedProject.storage = {
              ...project.storage,
              status: 'archive',
              storageHistory: project.storage?.storageHistory
                ? [...project.storage.storageHistory, newStorageHistoryEntry]
                : [newStorageHistoryEntry],
            };
          }
        }

        // Return the project with pre-calculated thresholds for the UI
        return {
          ...updatedProject,
          storage: {
            ...updatedProject.storage,
            archiveThreshold: archiveThresholdDate.getTime(),
            expiryDate: finalExpiryThresholdDate.getTime(),
          },
        };
      }
      return project;
    }));

    return convertTimestamps(processedProjects);
  }
);

export const restoreProject = createAsyncThunk(
  'projects/restoreProject',
  async ({ domain, projectId }, { dispatch }) => {
    await restoreProjectFromArchive(domain, projectId);
    dispatch(showAlert({ type: 'success', message: 'Project restored to Active storage for 30 days.' }));
    return { projectId };
  }
);

export const archiveProject = createAsyncThunk(
  'projects/archiveProject',
  async ({ domain, projectId }, { dispatch }) => {
    await updateProjectStorageToArchive(domain, projectId);
    dispatch(showAlert({ type: 'success', message: 'Project moved to Archive storage.' }));
    return { projectId };
  }
);

export const checkFirestoreConnection = createAsyncThunk(
  'projects/checkFirestoreConnection',
  async () => {
      return checkFirestoreConnection()
  }
)

export const addProject = createAsyncThunk(
  'projects/addProject',
  ({domain,projectData}) => {
    const data = addProjectToStudio(domain,projectData)
    return data}
);
export const updateProjectName = createAsyncThunk(
  'projects/updateProjectName',
  async ({ domain, projectId, newName }, { dispatch }) => {
    // Call Firestore function to update project name
    await updateProjectNameInFirestore(domain, projectId, newName);
    return { projectId, newName };
  }
);

export const updateProjectStatus = createAsyncThunk(
  'projects/updateProjectStatus',
  async ({ domain, projectId, newStatus }, { dispatch }) => {
    await updateProjectStatusInFirestore(domain, projectId, newStatus);
    return { projectId, newStatus };
  }
);

export const approveExtension = createAsyncThunk(
  'projects/approveExtension',
  async ({ domain, projectId }, { dispatch }) => {
    await approveExtensionRequest(domain, projectId);
    dispatch(showAlert({ type: 'success', message: 'Project validity extended by 3 months.' }));
    return { projectId };
  }
);
export const updateCollectionName = createAsyncThunk(
  'projects/updateCollectionName',
  async ({ domain, projectId, collectionId, newName }, { dispatch }) => {
    // Call Firestore function to update collection name
    await updateCollectionNameInFirestore(domain, projectId, collectionId, newName);
    return { projectId, collectionId, newName };
  }
);

export const updateCollectionStatus = createAsyncThunk(
  'projects/updateCollectionStatus',
  async ({ domain, projectId, collectionId, status,selectionGallery }, { dispatch }) => {
    await updateCollectionStatusByCollectionIdInFirestore(domain, projectId, collectionId, status,selectionGallery);
    return { projectId, collectionId, status,selectionGallery };
  }
);


export const updateSelectionGalleryStatus = createAsyncThunk(
  'projects/updateSelectionGalleryStatus',
  async ({ domain, projectId, collectionId, selectionGallery }, { dispatch }) => {
    if (selectionGallery) {
      await updateCollectionStatusByCollectionIdInFirestore(domain, projectId, collectionId, 'active', selectionGallery);
      // Update project status to active when selection is enabled
      await updateProjectStatusInFirestore(domain, projectId, 'active');
    } else {
      await updateCollectionStatusByCollectionIdInFirestore(domain, projectId, collectionId, 'visible', selectionGallery);
    }
    return { projectId, collectionId, selectionGallery };
  }
);
export const updateCollectionSelectionStatus = createAsyncThunk(
  'projects/updateCollectionSelectionStatus',
  async ({ domain, projectId, collectionId, status }, { dispatch }) => {
    await updateCollectionSelectionStatusByCollectionIdInFirestore(domain, projectId, collectionId, status);
    return { projectId, collectionId, status };
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  ({domain,bucketUrl,projectId}) => { 
    deleteProjectFromFirestore(domain,bucketUrl, projectId);
    return projectId;
  }
);

// Collections
export const addCollection = createAsyncThunk(
  'projects/addCollection',
  async ({ domain, projectId, newCollection }, { dispatch }) => {
    const id = await addCollectionToStudioProject(domain,projectId, newCollection);
    return convertTimestamps({projectId, collection: {id,...newCollection}})
  }
);

export const deleteCollection = createAsyncThunk(
  'projects/deleteCollection',
  async ({ domain,projectId, collectionId }, { dispatch }) => {
    await deleteCollectionFromFirestore(domain,projectId, collectionId);
    return { projectId, collectionId };
  }
);
// Event
export const addEvent = createAsyncThunk(
  'projects/addEvent',
  async ({ domain, projectId, newEvent }, { dispatch }) => {
    const id = await addEventToFirestore(domain,projectId, newEvent);
    return convertTimestamps({projectId:projectId, newEvent:{id, ...newEvent}})
  }
);
// Crew
export const addCrew = createAsyncThunk(
  'projects/addCrew',
  async ({ domain, projectId, eventId, crewData }, { dispatch }) => {
    await addCrewToFirestore(domain, projectId, eventId, crewData);
    return convertTimestamps({ projectId, eventId, crewData });
  }
);
// Financials
// Payments
export const addPayment =  createAsyncThunk(
  'projects/addPayment',
  async ({ domain,projectId, paymentData }, { dispatch }) => {
    await addPaymentToFirestore(domain, projectId, paymentData);
    return convertTimestamps({ projectId, paymentData });
  }
);
// Expenses
export const addExpense =  createAsyncThunk(
  'projects/addExpense',
  async ({ domain,projectId, paymentData }, { dispatch }) => {
    await addExpenseToFirestore(domain,projectId, paymentData);
    return convertTimestamps({ projectId, expenseData:paymentData });
  }
);
export const addBudget =  createAsyncThunk(
  'projects/addBudget',
  async ({ domain,projectId, budgetData }, { dispatch }) => {
    budgetData = await addBudgetToFirestore(domain, projectId, budgetData);
    return convertTimestamps({projectId,budgetData});
  }
);export const deleteFile = createAsyncThunk(
  'projects/deleteFile',
  async ({ studioName, projectId, collectionId, imageUrl, imageName }, { dispatch }) => {
    // Call the function to delete the file
    await deleteFileFromFirestoreAndStorage(studioName, projectId, collectionId, imageUrl, imageName);
    return { projectId, collectionId, fileName: imageName }; // Return necessary data
  }
);

export const toggleFileFavorite = createAsyncThunk(
  'projects/toggleFileFavorite',
  async ({ studioName, projectId, collectionId, imageUrl }, { dispatch }) => {
    const newStatus = await toggleFileFavoriteInFirestore(studioName, projectId, collectionId, imageUrl);
    return { projectId, collectionId, imageUrl, newStatus };
  }
);
// Invitations
export const updateInvitation = createAsyncThunk(
  'projects/updateInvitation',
  async ({ domain, projectId, invitationData }, { dispatch }) => {
    const updatedInvitationId = await updateInvitationInFirebase(domain, projectId, invitationData);
    return convertTimestamps({ projectId, invitationData, updatedInvitationId });
  }
);
export const fetchInvitation = createAsyncThunk(
  'invitations/fetchInvitation',
  async ({ domain, projectId }) => {
    const invitation = await fetchInvitationFromFirebase(domain, projectId);
    return convertTimestamps(invitation);
  }
);
export const logProjectActivity = createAsyncThunk(
  'projects/logProjectActivity',
  async ({ domain, projectId, activityEntry }) => {
    const activity = await addProjectActivityLogInFirestore(domain, projectId, activityEntry);
    return convertTimestamps({ projectId, activity });
  }
);
// Upload Cover
export const updateProjectCover = createAsyncThunk(
  "projects/updateProjectCover",
  async ({ domain, projectId, newCoverUrl, focusPoint }) => {
      const projectDocRef = doc(db, "studios", domain, "projects", projectId);
      await updateDoc(projectDocRef, { projectCover: newCoverUrl, focusPoint });
      return { projectId, newCoverUrl, focusPoint };
  }
);
// Sub Projects
export const createSubProject = createAsyncThunk(
  "projects/createSubProject",
  async ({ domain, parentProjectId, subProjectData }) => {
    const subProjectsCollectionRef = collection(
      db,
      "studios",
      domain,
      "projects",
      parentProjectId,
      "subProjects"
    );

    const subProjectDocRef = await addDoc(subProjectsCollectionRef, subProjectData);
    return { parentProjectId, subProjectId: subProjectDocRef.id, subProjectData };
  }
);





const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProjects_temp: (state, action) => {
      state.data = action.payload;
    },
    updateProjectsStatus: (state, action) => {
      state.status = action.payload;
    }
  
  },
  extraReducers: (builder) => {

    // Fetch projects
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      
      // Add project
      builder
      .addCase(addProject.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
     .addCase(addProject.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.data.push(action.payload);
        
      })
     .addCase(addProject.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      builder
      .addCase(updateProjectName.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(updateProjectName.fulfilled, (state, action) => {
        const { projectId, newName } = action.payload;
        state.data = state.data.map((project) => 
          project.id === projectId ? { ...project, name: newName } : project
        );
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(updateProjectName.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      builder
      .addCase(updateProjectStatus.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(updateProjectStatus.fulfilled, (state, action) => {
        const { projectId, newStatus } = action.payload;
        state.data = state.data.map((project) =>
          project.id === projectId ? { ...project, status: newStatus } : project
        );
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(updateProjectStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      // Delete project
      builder
      .addCase(deleteProject.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.status = 'succeeded';
        console.log("deleted id is "+action.payload)
        state.loading = false;
        state.data = state.data.filter(project => project.id !== action.payload);
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      // Add collection
      builder
      .addCase(addCollection.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(addCollection.fulfilled, (state, action) => {
        const { projectId, collection } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedCollections = [...project.collections, collection];
            return { ...project, collections: updatedCollections };
          }
          return project;
        });
        state.loading = false;
        state.status = 'succeeded';

      })
      .addCase(addCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete collection
      builder
      .addCase(deleteCollection.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, collectionId } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedCollections = project.collections.filter(collection => collection.id!== collectionId);
            return { ...project, collections: updatedCollections };
          }
        return project;
        }
      )})
      .addCase(deleteCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      })
      builder
      .addCase(updateCollectionName.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(updateCollectionName.fulfilled, (state, action) => {
        const { projectId, collectionId, newName } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedCollections = project.collections.map((collection) =>
              collection.id === collectionId ? { ...collection, name: newName } : collection
            );
            return { ...project, collections: updatedCollections };
          }
          return project;
        });
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(updateCollectionName.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      builder
      .addCase(updateCollectionStatus.pending, (state, action) => {
        const { collectionId } = action.meta.arg;
        state.updatingCollections[collectionId] = { ...state.updatingCollections[collectionId], status: true };
      })
      .addCase(updateCollectionStatus.fulfilled, (state, action) => {
        const { projectId, collectionId, status, selectionGallery } = action.payload;
        if (state.updatingCollections[collectionId]) {
          state.updatingCollections[collectionId].status = false;
        }
        const projectToUpdate = state.data.find((project) => project.id === projectId);
        if (projectToUpdate) {
          const collectionToUpdate = projectToUpdate.collections.find(
            (collection) => collection.id === collectionId
          );
          if (collectionToUpdate) {
            collectionToUpdate.status = status;
            collectionToUpdate.selectionGallery = selectionGallery;
          }
        }
      })
      .addCase(updateCollectionStatus.rejected, (state, action) => {
        const { collectionId } = action.meta.arg;
        if (state.updatingCollections[collectionId]) {
          state.updatingCollections[collectionId].status = false;
        }
        state.status = 'failed';
        state.error = action.error.message;
      });

      builder
      .addCase(updateSelectionGalleryStatus.pending, (state, action) => {
        const { collectionId } = action.meta.arg;
        state.updatingCollections[collectionId] = { ...state.updatingCollections[collectionId], selection: true };
      })
      .addCase(updateSelectionGalleryStatus.fulfilled, (state, action) => {
        const { projectId, collectionId, selectionGallery } = action.payload;
        if (state.updatingCollections[collectionId]) {
          state.updatingCollections[collectionId].selection = false;
        }
        console.log(selectionGallery)
        const projectToUpdate = state.data.find((project) => project.id === projectId);
        if (projectToUpdate) {
          // Update project status to active if selection is enabled
          if (selectionGallery) {
            projectToUpdate.status = 'active';
          }
          const collectionToUpdate = projectToUpdate.collections.find(
            (collection) => collection.id === collectionId
          );
          if (collectionToUpdate) {
            collectionToUpdate.selectionGallery = selectionGallery;
            if (selectionGallery) {
              collectionToUpdate.status = 'active';
            } else {
              collectionToUpdate.status = 'visible';
            }
          }
        }
      })
      .addCase(updateSelectionGalleryStatus.rejected, (state, action) => {
        const { collectionId } = action.meta.arg;
        if (state.updatingCollections[collectionId]) {
          state.updatingCollections[collectionId].selection = false;
        }
        state.status = 'failed';
        state.error = action.error.message;
      });
      
      builder
      .addCase(updateCollectionSelectionStatus.pending, (state) => {
      })
      .addCase(updateCollectionSelectionStatus.fulfilled, (state, action) => {
        const { projectId, collectionId, status } = action.payload;
        const projectToUpdate = state.data.find((project) => project.id === projectId);
        if (projectToUpdate) {
          const collectionToUpdate = projectToUpdate.collections.find(
            (collection) => collection.id === collectionId
          );
          if (collectionToUpdate) {
            collectionToUpdate.selectionStatus = status;
          }
        }
      })
      .addCase(updateCollectionSelectionStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });

      builder
      .addCase(deleteFile.pending, (state) => {
      })
      
      .addCase(deleteFile.fulfilled, (state, action) => {
        const { projectId, collectionId, fileName, photoId } = action.payload;
        const project = state.data.find(p => p.id === projectId);
        if (project) {
          const collection = project.collections.find(c => c.id === collectionId);
          if (collection) {
            // New model: remove photoId from photoIds[]
            if (photoId && collection.photoIds) {
              collection.photoIds = collection.photoIds.filter(id => id !== photoId);
            }
            // Legacy model: remove from uploadedFiles[]
            if (collection.uploadedFiles) {
              collection.uploadedFiles = collection.uploadedFiles.filter(f => f.name !== fileName);
            }
          }
        }
      })
    
      .addCase(deleteFile.rejected, (state, action) => {
        state.error = action.error.message;
      });

      builder
      .addCase(toggleFileFavorite.fulfilled, (state, action) => {
        const { projectId, collectionId, imageUrl, newStatus, photoId } = action.payload;
        const project = state.data.find(p => p.id === projectId);
        if (project) {
          const collection = project.collections.find(c => c.id === collectionId);
          if (collection) {
            // New model: update status on photoIds reference (lightweight)
            if (photoId && collection.photoIds) {
              // status lives on the Project Photo; collection only holds references
            }
            // Legacy model: update status in uploadedFiles[]
            if (collection.uploadedFiles) {
              const file = collection.uploadedFiles.find(f => f.url === imageUrl);
              if (file) {
                file.status = newStatus;
              }
            }
          }
        }
      });

      // Add Event
      builder
      .addCase(addEvent.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(addEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, newEvent } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedEvents = [...project.events, convertTimestamps(newEvent)];
            return { ...project, events: updatedEvents };
          }
          return project;
        });
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      })
      // Add Crue
      builder
      .addCase(addCrew.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(addCrew.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, eventId, crewData } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedEvents = project.events.map((event) => {
              if (event.id === eventId) {
                const updatedCrew = [...event.crews, crewData];
                return { ...event, crews: updatedCrew };
              }
              return event;
            });
            return { ...project, events: updatedEvents };
          }
          return project;
        });
      })
      .addCase(addCrew.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      // Add payment
      builder
      .addCase(addPayment.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
     .addCase(addPayment.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, paymentData } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedPayments = [...project.payments, paymentData];
            return { ...project, payments: updatedPayments };
          }
          return project;
        });
      })
     .addCase(addPayment.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
      // Add Expense
      builder
      .addCase(addExpense.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, expenseData } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const updatedExpenses = [...project.expenses, expenseData];
            return { ...project, expenses: updatedExpenses };
          }
          return project;
        });
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });


      // set Budget
      builder
     .addCase(addBudget.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(addBudget.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, budgetData } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            return { ...project, budgets: budgetData };
          }
          return project;
        });
      })
     .addCase(addBudget.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });

      // INVITATION
      // Add the cases to the slice reducer
      builder
      .addCase(updateInvitation.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(updateInvitation.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        const { projectId, invitationData } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            return { ...project, invitation: { ...project.invitation, ...invitationData } };
          }
          return project;
        });
        console.log('Invitation data updated:', invitationData);
      })
      .addCase(updateInvitation.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchInvitation.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        //find project and add invitation
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            return { ...project, invitation: action.payload.invitation };
          }
          return project;
        });
      })
      .addCase(logProjectActivity.fulfilled, (state, action) => {
        const { projectId, activity } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const currentLog = project.activityLog || [];
            return { ...project, activityLog: [...currentLog, activity] };
          }
          return project;
        });
      });

      // update project cover
      builder
        // Handle pending state
        .addCase(updateProjectCover.pending, (state) => {
            state.loading = true;
        })
        // Handle fulfilled state
        .addCase(updateProjectCover.fulfilled, (state, action) => {
            const { projectId, newCoverUrl, focusPoint } = action.payload;
            if(projectId){
                state.data = state.data.map((project) => {
                    if (project.id === projectId) {
                        return { ...project, projectCover: newCoverUrl, focusPoint };
                    }
                    return project;
                });
            }
            state.loading = false;
            state.error = null;
        })
        // Handle rejected state
        .addCase(updateProjectCover.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        builder
      .addCase(createSubProject.fulfilled, (state, action) => {
        const { parentProjectId, subProjectId, subProjectData } = action.payload;
        const parentProject = state.data.find((p) => p.id === parentProjectId);
        if (parentProject) {
          if (!parentProject.subProjects) {
            parentProject.subProjects = [];
          }
          parentProject.subProjects.push({
            id: subProjectId,
            ...subProjectData,
          });
        }
      });

      builder
      .addCase(restoreProject.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        const now = Date.now();
        state.data = state.data.map((project) =>
          project.id === projectId
            ? {
                ...project,
                storage: {
                  ...project.storage,
                  status: 'active',
                  lastRestoredAt: now,
                  storageHistory: [
                    ...(project.storage?.storageHistory || []),
                    { status: 'active', dateMoved: now }
                  ]
                }
              }
            : project
        );
      })
      .addCase(archiveProject.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        const now = Date.now();
        state.data = state.data.map((project) =>
          project.id === projectId
            ? {
                ...project,
                storage: {
                  ...project.storage,
                  status: 'archive',
                  storageHistory: [
                    ...(project.storage?.storageHistory || []),
                    { status: 'archive', dateMoved: now }
                  ]
                }
              }
            : project
        );
      })
      .addCase(approveExtension.fulfilled, (state, action) => {
        const { projectId } = action.payload;
        state.data = state.data.map((project) => {
          if (project.id === projectId) {
            const currentValidity = parseInt(project.projectValidityMonths || '6');
            const newValidity = currentValidity + 3;
            
            // Recalculate archiveThreshold
            const archiveThresholdDate = new Date(project.createdAt);
            archiveThresholdDate.setMonth(archiveThresholdDate.getMonth() + newValidity);

            return {
              ...project,
              projectValidityMonths: newValidity,
              storage: {
                ...project.storage,
                archiveThreshold: archiveThresholdDate.getTime(),
              }
            };
          }
          return project;
        });
      });
  },
});

export const { setProjects_temp,updateProjectsStatus } = projectsSlice.actions;
export default projectsSlice.reducer;

// Selector to get projects data from the state
export const selectProjects = (state) => state.projects.data;
export const selectProjectsStatus = (state) => state.projects.status;
export const selectLoading = (state) => state.projects.loading;
export const selectUpdatingCollections = (state) => state.projects.updatingCollections;
